import { FC } from 'react';
import { Stack } from '@mui/material';
import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { APP, ROUTES } from 'config';
import {
  adminOrdersFiltersSchema,
  COLOR_BY_STATUS,
  useAdminOrders,
} from 'store/admin-orders';
import { useSearchParams } from 'utils/navigation';
import { useRouteParams } from 'utils/useIdParam';
import { formatCurrency } from 'utils/format';

export const AdminShopUserDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: shopUserId } = useRouteParams(ROUTES.admin.shopUsers.details);
  const { searchParams: filters, navigate } = useSearchParams(
    adminOrdersFiltersSchema,
  );

  const { data: orders, isLoading } = useAdminOrders({
    ...filters,
    shopUserIds: [shopUserId],
    paymentType: ['mixed', 'personal'],
  });

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={orders?.total} />
        </Stack>
      </TableToolbar>

      <Table
        data={orders?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'createdAt',
            header: t('marketplaceOrders.orderDate'),
            cell: ({ row: { original: order } }) =>
              moment(order.createdAt).format('l LT'),
          },
          {
            id: 'orderNr',
            header: t('marketplaceOrders.orderId'),
            cell: ({ row: { original: order } }) => (
              <Link
                label={order.orderNr}
                to={ROUTES.admin.orders.details.replace(':id', order.id)}
              />
            ),
          },
          {
            id: 'item',
            header: t('marketplaceItems.name'),
            cell: ({ row: { original: order } }) => (
              <Link
                label={order.shopItem.name}
                to={ROUTES.admin.shopItems.details.settings.replace(
                  ':id',
                  order.shopItem.id,
                )}
              />
            ),
          },
          {
            id: 'paymentType',
            header: t('orders.paymentType.label'),
            cell: ({ row: { original: order } }) =>
              t(`orders.paymentType.${order.paymentType}`),
          },
          {
            id: 'employeePaymentAmount',
            header: t('orders.paymentDetails.shopUserPrice', {
              defaultValue: 'Shop user price',
            }),
            cell: ({ row: { original: order } }) =>
              formatCurrency(
                i18n.language,
                order.employeePaymentAmount,
                order.currency,
              ),
            meta: { align: 'right' },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: order } }) => (
              <Typography color={COLOR_BY_STATUS[order.status]}>
                {t(`marketplaceOrders.status-${order.status}`)}
              </Typography>
            ),
          },
        ]}
        pagination={
          orders && {
            count: orders.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
